import React from "react";
import { Link } from "react-router-dom";
import { Building2, MapPin, ExternalLink, Bookmark, BookmarkCheck, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { shareJob } from "@/lib/share";
import { getLocationBadge } from "@/lib/countryBadge";

function formatSalary(min, max, currency) {
  if (!min && !max) return null;
  const fmt = (n) => {
    if (!n) return "";
    if (currency === "INR") return `₹${(n / 100000).toFixed(0)}L`;
    return `$${n.toLocaleString()}`;
  };
  if (min && max) return `${fmt(min)} – ${fmt(max)}`;
  return fmt(min || max);
}

export default function JobCard({ job, onSave, saved, compact = false, recommended = false }) {
  const locked = job.locked;
  const salary = formatSalary(job.salary_min, job.salary_max, job.currency);
  const to = `/jobs/${job.job_id}${recommended ? "?rec=1" : ""}`;
  const badge = getLocationBadge(job);
  return (
    <article
      className={`vital-card p-5 flex flex-col gap-3 group ${compact ? "" : "h-full"}`}
      data-testid={`job-card-${job.job_id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-10 w-10 rounded-md bg-muted flex items-center justify-center shrink-0 overflow-hidden border border-border">
            <Building2 className="h-5 w-5 text-muted-foreground" />
          </div>
          <div className="min-w-0">
            <div className="text-xs font-semibold text-muted-foreground truncate">{job.company}</div>
            {job.level && <div className="eyebrow truncate">{job.level}</div>}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={(e) => { e.preventDefault(); shareJob(job); }}
            className="p-1 rounded hover:bg-muted"
            data-testid={`share-job-${job.job_id}`}
            aria-label="Share job"
          >
            <Share2 className="h-4 w-4 text-muted-foreground" />
          </button>
          {onSave && !locked && (
            <button
              onClick={(e) => { e.preventDefault(); onSave(job.job_id, !saved); }}
              className="p-1 rounded hover:bg-muted"
              data-testid={`save-job-${job.job_id}`}
              aria-label="Save job"
            >
              {saved ? <BookmarkCheck className="h-4 w-4 text-primary" /> : <Bookmark className="h-4 w-4 text-muted-foreground" />}
            </button>
          )}
        </div>
      </div>

      <Link to={to} className="block" data-testid={`job-title-${job.job_id}`}>
        <h3 className="text-lg font-bold leading-snug tracking-tight group-hover:text-primary transition-colors">
          {job.title}
        </h3>
      </Link>

      <div className="flex flex-wrap gap-1.5 text-xs">
        {badge && (
          <span className="pill" data-testid={`country-badge-${job.job_id}`} title={badge.label}>
            <span className="mr-1" aria-hidden="true">{badge.flag}</span>{badge.label}
          </span>
        )}
        <span className="pill"><MapPin className="h-3 w-3 mr-1" />{job.location}</span>
        <span className="pill">{job.type}</span>
        {salary && <span className="pill font-semibold text-primary">{salary}</span>}
      </div>

      {!compact && job.skills?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-1">
          {job.skills.slice(0, 4).map((s) => (
            <span key={s} className="text-[11px] px-2 py-0.5 rounded bg-muted text-muted-foreground">{s}</span>
          ))}
        </div>
      )}

      {!compact && (
        <p className={`text-sm text-muted-foreground line-clamp-2 mt-1 ${locked ? "blur-[2px] select-none" : ""}`}>
          {locked ? "Full description is unlocked when you create your free KARM account. The role is real, the company is real — sign up takes 10 seconds." : job.description}
        </p>
      )}

      <div className="mt-auto pt-3 flex items-center justify-between">
        <Button asChild variant="outline" size="sm" data-testid={`view-job-${job.job_id}`}>
          <Link to={to}>{locked ? "Preview" : "View role"}</Link>
        </Button>
        {locked ? (
          <Link
            to="/auth"
            className="text-xs font-semibold inline-flex items-center gap-1 text-primary hover:underline"
            data-testid={`unlock-job-${job.job_id}`}
          >
            Sign up to apply <ExternalLink className="h-3 w-3" />
          </Link>
        ) : job.apply_url && (
          <a
            href={job.apply_url}
            target="_blank"
            rel="noreferrer"
            className="text-xs font-semibold inline-flex items-center gap-1 text-primary hover:underline"
            data-testid={`apply-external-${job.job_id}`}
          >
            Apply on company site <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
    </article>
  );
}
